import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, getTodayISO } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReceiveMaterial() {
  const [pendingItems, setPendingItems] = useState([]);
  const [materialReceived, setMaterialReceived] = useState([]);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [receiveForm, setReceiveForm] = useState({
    quantity_received: '',
    received_date: getTodayISO(),
    challan_number: '',
    remarks: ''
  });

  useEffect(() => {
    fetchPendingItems();
    fetchMaterialReceived();
  }, []);

  const fetchPendingItems = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/pos`);
      // Filter only Approved POs that are not completed
      const approved = response.data.filter(po => 
        po.approval_status === 'Approved' && po.status !== 'Completed' && po.status !== 'Rejected'
      );
      
      // Build list of items pending receipt
      const itemsPending = [];
      for (const po of approved) {
        try {
          const detailsRes = await axios.get(`${API}/jobwork/pos/${po.id}`);
          const details = detailsRes.data;
          
          // Get items that have pending material to receive
          if (details.items_status) {
            for (const item of details.items_status) {
              // For Job Work: can receive only after material is sent
              // For Direct Supply: can receive directly
              let canReceive = false;
              let pendingQty = item.pending;
              
              if (item.item_type === 'Job Work') {
                // Job Work: material must be sent first before receiving
                if (item.sent > item.received) {
                  canReceive = true;
                  pendingQty = item.sent - item.received; // Can only receive what was sent
                }
              } else {
                // Direct Supply: can receive full quantity
                canReceive = pendingQty > 0;
              }
              
              if (canReceive && pendingQty > 0) {
                itemsPending.push({
                  po_id: po.id,
                  po_number: po.po_number,
                  vendor_name: po.vendor_name,
                  po_item_id: item.id,
                  partcode: item.partcode,
                  description: item.description,
                  item_type: item.item_type,
                  job_work_type: item.job_work_type,
                  total_quantity: item.quantity,
                  sent: item.sent,
                  received: item.received,
                  pending_to_receive: pendingQty
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching PO details:', error);
        }
      }
      setPendingItems(itemsPending);
    } catch (error) {
      console.error('Error fetching pending items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialReceived = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/material-received`);
      setMaterialReceived(response.data);
    } catch (error) {
      console.error('Error fetching material received:', error);
    }
  };

  const handleReceiveClick = (item) => {
    setSelectedItem(item);
    setReceiveForm({
      quantity_received: '',
      received_date: getTodayISO(),
      challan_number: '',
      remarks: ''
    });
    setShowReceiveModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/jobwork/material-received`, {
        po_id: selectedItem.po_id,
        po_number: selectedItem.po_number,
        vendor_name: selectedItem.vendor_name,
        partcode: selectedItem.partcode,
        description: selectedItem.description,
        quantity_received: parseInt(receiveForm.quantity_received),
        received_date: receiveForm.received_date,
        challan_number: receiveForm.challan_number || null,
        remarks: receiveForm.remarks || null
      });
      setShowReceiveModal(false);
      resetForm();
      fetchPendingItems();
      fetchMaterialReceived();
      alert('Material received recorded successfully!');
    } catch (error) {
      console.error('Error recording material received:', error);
      alert('Error recording material received: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setReceiveForm({
      quantity_received: '',
      received_date: getTodayISO(),
      challan_number: '',
      remarks: ''
    });
    setSelectedItem(null);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="receive-material">
      <h1 className="text-3xl font-bold text-gray-900">Receive Material (Daily Incoming)</h1>
      <p className="text-gray-600">Record materials received from vendors. Job Work items can be received only after material is sent.</p>

      {/* Pending Items to Receive */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Pending Material to Receive</h2>
          <p className="text-sm text-gray-600 mt-1">Items ready to be received from vendors</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Received</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    No pending material to receive. All items have been received.
                  </td>
                </tr>
              ) : (
                pendingItems.map((item, idx) => (
                  <tr key={`${item.po_id}-${item.po_item_id}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">{item.po_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.vendor_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.partcode}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.description}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.item_type === 'Job Work' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {item.item_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">{item.total_quantity}</td>
                    <td className="px-6 py-4 text-sm text-right text-yellow-600">{item.sent}</td>
                    <td className="px-6 py-4 text-sm text-right text-green-600">{item.received}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">{item.pending_to_receive}</td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleReceiveClick(item)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition text-sm"
                      >
                        Receive
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Material Received History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Material Received History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Received</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan No.</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materialReceived.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No material received yet.
                  </td>
                </tr>
              ) : (
                materialReceived.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">{formatDate(record.received_date)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.po_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{record.vendor_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.partcode}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">{record.quantity_received}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{record.challan_number || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Receive Material Modal */}
      {showReceiveModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Receive Material from Vendor</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Item Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Item Details:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>PO Number:</strong> {selectedItem.po_number}</div>
                  <div><strong>Vendor:</strong> {selectedItem.vendor_name}</div>
                  <div><strong>Part Code:</strong> {selectedItem.partcode}</div>
                  <div><strong>Description:</strong> {selectedItem.description}</div>
                  <div><strong>Type:</strong> 
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                      selectedItem.item_type === 'Job Work' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedItem.item_type}
                    </span>
                  </div>
                  <div><strong>PO Quantity:</strong> {selectedItem.total_quantity}</div>
                  <div><strong>Material Sent:</strong> <span className="text-yellow-600">{selectedItem.sent}</span></div>
                  <div><strong>Already Received:</strong> <span className="text-green-600">{selectedItem.received}</span></div>
                  <div className="col-span-2"><strong>Pending to Receive:</strong> <span className="text-red-600 font-semibold">{selectedItem.pending_to_receive}</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Received *</label>
                  <input
                    type="date"
                    value={receiveForm.received_date}
                    onChange={(e) => setReceiveForm({ ...receiveForm, received_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Received *</label>
                  <input
                    type="number"
                    value={receiveForm.quantity_received}
                    onChange={(e) => setReceiveForm({ ...receiveForm, quantity_received: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="1"
                    max={selectedItem.pending_to_receive}
                    placeholder={`Max: ${selectedItem.pending_to_receive}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Challan Number</label>
                <input
                  type="text"
                  value={receiveForm.challan_number}
                  onChange={(e) => setReceiveForm({ ...receiveForm, challan_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={receiveForm.remarks}
                  onChange={(e) => setReceiveForm({ ...receiveForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowReceiveModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Record Material Received
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
